import React from 'react';
import {
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
} from '@mui/material';
import { SEOHead } from '../../components/SEO';
import {
  CheckCircle,
  ArrowForward,
  Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  // Features configuration matching subscription tiers from BillingSetup
  const PLAN_FEATURES = {
    starter: [
      'Email-only integrations (Exchange, Gmail)',
      'Core AI teammates (Alex, Maya, Casey)',
      'Basic task management',
      'Knowledge graph (1-year email history)',
      'Standard support'
    ],
    professional: [
      'Everything in Starter',
      'Full Microsoft Graph integration',
      'Full Google Workspace integration',
      'Advanced AI teammates',
      'Workflow automation',
      'Priority support'
    ],
    industry_specific: [
      'Everything in Professional',
      'MEP Toolkit (Engineering)',
      'HVAC/Electrical calculations',
      'CAD file processing',
      'Industry compliance tools',
      'Dedicated support'
    ]
  };

  const plans = [
    {
      name: 'Starter',
      price: 29,
      description: 'Perfect for individuals and small teams getting started',
      features: PLAN_FEATURES.starter,
      buttonText: 'Get Started',
      buttonVariant: 'outlined' as const,
      popular: false,
    },
    {
      name: 'Professional',
      price: 59,
      description: 'Complete productivity suite for growing teams',
      features: PLAN_FEATURES.professional,
      buttonText: 'Get Started',
      buttonVariant: 'contained' as const,
      popular: true,
    },
    {
      name: 'Industry-Specific Toolkits',
      price: 129,
      description: 'Professional tools for specialized industries',
      features: PLAN_FEATURES.industry_specific,
      buttonText: 'Get Started',
      buttonVariant: 'outlined' as const,
      popular: false,
    },
  ];

  // SEO structured data for pricing page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Prismatic Forge Pricing",
    "description": "Choose the perfect plan for your productivity needs. Flexible pricing from $29/month with 30-day free trial.",
    "url": "https://prismaticforge.ai/pricing",
    "mainEntity": {
      "@type": "Product",
      "name": "Prismatic Forge",
      "offers": plans.map(plan => ({
        "@type": "Offer",
        "name": plan.name,
        "price": plan.price,
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "RecurringPriceSpecification",
          "billingFrequency": "monthly"
        }
      }))
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <SEOHead
        title="Pricing - Prismatic Forge"
        description="Choose the perfect plan for your productivity needs. Flexible pricing starting at $29/month with 30-day free trial. No setup fees, cancel anytime."
        keywords="pricing, plans, productivity software, AI teammates, knowledge management, subscription"
        ogTitle="Prismatic Forge Pricing - Choose Your Perfect Plan"
        ogDescription="Flexible pricing starting at $29/month. 30-day free trial, no setup fees, cancel anytime."
        canonical="https://prismaticforge.ai/pricing"
        jsonLd={structuredData}
      />

      <Container maxWidth="lg" style={{ paddingTop: 64, paddingBottom: 64 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            style={{
              fontWeight: 'bold',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              marginBottom: 24,
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography
            variant="h5"
            style={{
              color: 'rgba(0,0,0,0.6)',
              maxWidth: '800px',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginBottom: 32,
              fontWeight: 300,
            }}
          >
            Flexible pricing to match your needs, from individual creators to enterprise teams.
            All plans include a 30-day free trial.
          </Typography>
        </div>

        {/* Pricing Cards */}
        <Grid container spacing={4} style={{ marginBottom: 64 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.popular ? '2px solid' : '1px solid',
                  borderColor: plan.popular ? '#325BA8' : 'rgba(0,0,0,0.12)',
                  position: 'relative',
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    icon={<Star />}
                    style={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#325BA8',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}
                <CardContent style={{ flex: 1, padding: 32 }}>
                  <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 8, textAlign: 'center' }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="h2" style={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    color: plan.popular ? '#325BA8' : 'inherit',
                    marginBottom: 16,
                  }}>
                    ${plan.price}
                    <Typography component="span" variant="h6" style={{ color: 'rgba(0,0,0,0.6)' }}>
                      /month per user
                    </Typography>
                  </Typography>
                  <Typography variant="body1" style={{
                    textAlign: 'center',
                    marginBottom: 32,
                    color: 'rgba(0,0,0,0.6)',
                    minHeight: '3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {plan.description}
                  </Typography>

                  <List style={{ marginBottom: 32 }}>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                        <ListItemIcon style={{ minWidth: 36 }}>
                          <CheckCircle style={{ color: '#4DC4D2', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: feature.startsWith('Everything in') ? 600 : 400,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    variant={plan.buttonVariant}
                    fullWidth
                    size="large"
                    style={{
                      paddingTop: 12,
                      paddingBottom: 12,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      ...(plan.popular && { backgroundColor: '#325BA8' }),
                    }}
                    onClick={() => navigate('/signup')}
                    endIcon={<ArrowForward />}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Features Comparison */}
        <div style={{ marginBottom: 64 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold' }}
          >
            What's Included
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card style={{ height: '100%', padding: 24 }}>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', color: '#4DC4D2' }}>
                  Core Features
                </Typography>
                <List dense>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="AI-powered knowledge management" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Context-enhanced task management" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Semantic knowledge graph" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Real-time collaboration" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card style={{ height: '100%', padding: 24 }}>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', color: '#325BA8' }}>
                  AI Teammates
                </Typography>
                <List dense>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Alex - Managing Executive" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Maya - Email & Analytics Specialist" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Casey - Daily Awareness Assistant" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Create unlimited custom teammates" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card style={{ height: '100%', padding: 24 }}>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', color: '#E94257' }}>
                  Enterprise Ready
                </Typography>
                <List dense>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Enterprise-grade security" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Microsoft Graph integration" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Google Workspace integration" />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <ListItemIcon><CheckCircle style={{ color: '#4DC4D2' }} /></ListItemIcon>
                    <ListItemText primary="Advanced analytics & insights" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </div>

        {/* FAQ Section */}
        <div style={{ marginBottom: 64 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold' }}
          >
            Frequently Asked Questions
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: 32 }}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                  How does the free trial work?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You get full access to all features for 30 days. No charges during the trial period.
                  You can cancel anytime without being charged.
                </Typography>
              </div>

              <div style={{ marginBottom: 32 }}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                  Can I change plans later?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes! You can upgrade, downgrade, or cancel your plan at any time.
                  Changes take effect at the next billing cycle.
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: 32 }}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                  What payment methods do you accept?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We accept all major credit cards and debit cards through our secure payment processor, Stripe.
                </Typography>
              </div>

              <div style={{ marginBottom: 32 }}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                  Is there a setup fee?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No setup fees ever. You only pay for your monthly or annual subscription after your free trial ends.
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>

        {/* CTA Section */}
        <div style={{
          textAlign: 'center',
          paddingTop: 64,
          paddingBottom: 64,
          backgroundColor: '#fff',
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,0.12)',
        }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            style={{ fontWeight: 'bold', marginBottom: 24 }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="h6"
            style={{ marginBottom: 32, color: 'rgba(0,0,0,0.6)', fontWeight: 300 }}
          >
            Join thousands of knowledge workers who have transformed their productivity
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            style={{
              backgroundColor: '#4DC4D2',
              color: '#0C1319',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
            endIcon={<ArrowForward />}
          >
            Start Your Free Trial
          </Button>
          <Typography
            variant="body2"
            style={{ marginTop: 16, color: 'rgba(0,0,0,0.6)' }}
          >
            30-day free trial • No credit card required • Cancel anytime
          </Typography>
        </div>

        <Divider style={{ marginTop: 48, marginBottom: 48 }} />

        {/* Footer Links */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            Questions? Contact us at{' '}
            <Typography
              component="a"
              href="mailto:support@prismaticforge.ai"
              style={{ color: '#325BA8', textDecoration: 'none' }}
            >
              support@prismaticforge.ai
            </Typography>
          </Typography>
          <div style={{
            display: 'flex',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <Typography
              component="a"
              href="/terms"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              component="a"
              href="/privacy/"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Typography>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PricingPage;
